import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
    BarChart2, Calendar, Building, User, Filter, ChevronDown, 
    Download, FileText, Printer, RefreshCw, Search, X, DollarSign,
    TrendingUp, TrendingDown, Users, Briefcase, Clipboard, Phone,
    Save, Copy, ArrowRight, Package, PieChart, Zap, CreditCard, Wallet
} from 'lucide-react';
import * as XLSX from 'xlsx';
const API_BASE_URL = ''; // السيرفر على نفس الموقع

const FinancialReports = () => {
    // States
    const [activeTab, setActiveTab] = useState('clients'); // clients, delegates, company, treasury
    const [companies, setCompanies] = useState([]);
    const [delegates, setDelegates] = useState([]);
    const [professions, setProfessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summaryData, setSummaryData] = useState({
        totalClients: 0,
        totalBalance: 0,
        totalDeposits: 0,
        totalWithdrawals: 0
    });
    const [toast, setToast] = useState(null);
    // إضافة حالة جديدة لتخزين بيانات مستحقات العملاء
    const [clientDues, setClientDues] = useState([]);

    // فلاتر تقارير العملاء
    const [clientFilters, setClientFilters] = useState({
        companyId: '',
        delegateId: '',
        dateFrom: '',
        dateTo: '',
        professionId: '',
        hasTransactions: '',
        nationalId: '',
        balanceType: 'all', // all, positive, negative, zero
        netWorkDateFrom: '', // تاريخ النت (من)
        netWorkDateTo: '', // تاريخ النت (إلى)
        visaDateFrom: '', // تاريخ التأشير (من)
        visaDateTo: '', // تاريخ التأشير (إلى)
        travelDateFrom: '', // تاريخ السفر (من)
        travelDateTo: '', // تاريخ السفر (إلى)
    });
    

    // فلاتر تقارير المندوبين
    const [delegateFilters, setDelegateFilters] = useState({
        delegateId: '',
        dateFrom: '',
        dateTo: ''
    });

    // فلاتر تقارير الشركة
    const [companyFilters, setCompanyFilters] = useState({
        companyId: '',
        dateFrom: '',
        dateTo: '',
        transactionType: '' // income, expense
    });

    // فلاتر تقارير الخزينة
    const [treasuryFilters, setTreasuryFilters] = useState({
        dateFrom: '',
        dateTo: '',
        transactionType: '' // deposit, withdrawal
    });

    const printRef = useRef();

    // Effects
    useEffect(() => {
        fetchCompanies();
        fetchDelegates();
        fetchProfessions();
    }, []);

    useEffect(() => {
        if (activeTab) {
            resetFilters();
            setReportData([]);
            setSummaryData({
                totalClients: 0,
                totalBalance: 0,
                totalDeposits: 0,
                totalWithdrawals: 0
            });
        }
    }, [activeTab]);

    // Helpers
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const resetFilters = () => {
        switch (activeTab) {
            case 'clients':
                setClientFilters({
                    companyId: '',
                    delegateId: '',
                    dateFrom: '',
                    dateTo: '',
                    professionId: '',
                    hasTransactions: '',
                    nationalId: '',
                    balanceType: 'all',
                    netWorkDateFrom: '',
                    netWorkDateTo: '',
                    visaDateFrom: '',
                    visaDateTo: '',
                    travelDateFrom: '',
                    travelDateTo: '',
                });
                break;
            case 'delegates':
                setDelegateFilters({
                    delegateId: '',
                    dateFrom: '',
                    dateTo: ''
                });
                break;
            case 'company':
                setCompanyFilters({
                    companyId: '',
                    dateFrom: '',
                    dateTo: '',
                    transactionType: ''
                });
                break;
            case 'treasury':
                setTreasuryFilters({
                    dateFrom: '',
                    dateTo: '',
                    transactionType: ''
                });
                break;
            default:
                break;
        }
    };

    // طرق تنسيق البيانات
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '-';
        return amount.toLocaleString('ar-SA') + ' ريال';
    };

    const formatPhoneNumber = (phoneNumbers) => {
        if (!phoneNumbers) return '-';
        
        try {
            let numbers = [];
            
            if (typeof phoneNumbers === 'string') {
                try {
                    const parsed = JSON.parse(phoneNumbers);
                    
                    if (Array.isArray(parsed)) {
                        numbers = parsed.filter(n => n && n.length > 0);
                    } else if (typeof parsed === 'object' && parsed !== null) {
                        numbers = Object.values(parsed).filter(n => n && n.length > 0);
                    } else if (parsed) {
                        numbers = [parsed];
                    }
                } catch (e) {
                    if (phoneNumbers !== '[]') {
                        numbers = [phoneNumbers];
                    }
                }
            } else if (Array.isArray(phoneNumbers)) {
                numbers = phoneNumbers.filter(n => n && n.length > 0);
            } else if (typeof phoneNumbers === 'object' && phoneNumbers !== null) {
                numbers = Object.values(phoneNumbers).filter(n => n && n.length > 0);
            }
            
            return numbers.length > 0 ? numbers.join(' ، ') : '-';
        } catch (error) {
            console.error('خطأ في معالجة رقم الهاتف:', error);
            return '-';
        }
    };

    // API Functions
    // في دالة fetchCompanies
    const fetchCompanies = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/companies`);
            if (!response.ok) throw new Error('فشل في جلب بيانات الشركات');
            
            const data = await response.json();
            console.log('تم جلب الشركات:', data);
            setCompanies(data);
            return data;
        } catch (error) {
            console.error('خطأ في جلب بيانات الشركات:', error);
            showToast('فشل في جلب بيانات الشركات', 'error');
            return [];
        }
    };

    // نضيف هذه الدالة لجلب مبالغ الدفع للشركات
    const fetchCompanyPaymentAmounts = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/companies`);
            if (!response.ok) throw new Error('فشل في جلب بيانات الشركات ومبالغ الدفع');
            
            const data = await response.json();
            console.log('بيانات الشركات ومبالغ الدفع:', data);
            
            // تحديث حالة الشركات مع معلومات مبالغ الدفع
            setCompanies(data);
            
            return data;
        } catch (error) {
            console.error('خطأ في جلب بيانات مبالغ الدفع:', error);
            showToast('فشل في جلب بيانات مبالغ الدفع', 'error');
            return [];
        }
    };

    // نعدل دالة fetchDelegates
    const fetchDelegates = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/delegates`);
            if (!response.ok) throw new Error('فشل في جلب بيانات المندوبين');
            
            const data = await response.json();
            console.log('تم جلب المندوبين:', data);
            setDelegates(data);
        } catch (error) {
            console.error('خطأ في جلب بيانات المندوبين:', error);
            showToast('فشل في جلب بيانات المندوبين', 'error');
        }
    };

    const fetchProfessions = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/professions`);
            if (!response.ok) throw new Error('فشل في جلب بيانات المهن');
            
            const data = await response.json();
            setProfessions(data);
        } catch (error) {
            console.error('خطأ في جلب بيانات المهن:', error);
            showToast('فشل في جلب بيانات المهن', 'error');
        }
    };

    // نضيف دالة جديدة لحساب المستحقات المالية للعملاء
    const calculateClientDues = async () => {
        try {
            // جلب العملاء
            if (reportData.length === 0) {
                await fetchClientReport();
            }
            
            // جلب بيانات مبالغ الدفع للشركات
            const companiesWithPayments = await fetchCompanyPaymentAmounts();
            
            // حساب المستحقات لكل عميل
            const clientsWithDues = reportData.map(client => {
                // البحث عن الشركة المرتبطة بالعميل
                const clientCompany = companiesWithPayments.find(
                    company => company.CompanyName === client.CompanyName
                );
                
                // مبلغ الدفع المطلوب من الشركة (إذا وجد)
                const requiredPayment = clientCompany?.PaymentAmount || 0;
                
                // المبلغ المدفوع فعلياً من العميل
                const paidAmount = client.TotalDeposits || 0;
                
                // المستحقات المتبقية
                const remainingDue = requiredPayment - paidAmount;
                
                // إضافة معلومات المستحقات للعميل
                return {
                    ...client,
                    RequiredPayment: requiredPayment,
                    PaidAmount: paidAmount,
                    RemainingDue: remainingDue
                };
            });
            
            setClientDues(clientsWithDues);
            showToast('تم حساب المستحقات المالية بنجاح', 'success');
            
        } catch (error) {
            console.error('خطأ في حساب المستحقات المالية:', error);
            showToast('فشل في حساب المستحقات المالية', 'error');
        }
    };

    // جلب تقارير العملاء
    const fetchClientReport = async () => {
        setLoading(true);
        
        // إعداد مؤقت الانتظار - 30 ثانية
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        try {
            let url = `${API_BASE_URL}/api/reports/clients?`;
            
            // إضافة معايير الفلترة
            if (clientFilters.companyId) {
                url += `companyId=${clientFilters.companyId}&`;
            }
            if (clientFilters.delegateId) {
                url += `delegateId=${clientFilters.delegateId}&`;
            }
            if (clientFilters.dateFrom) {
                url += `dateFrom=${clientFilters.dateFrom}&`;
            }
            if (clientFilters.dateTo) {
                url += `dateTo=${clientFilters.dateTo}&`;
            }
            if (clientFilters.professionId) {
                url += `professionId=${clientFilters.professionId}&`;
            }
            if (clientFilters.hasTransactions) {
                url += `hasTransactions=${clientFilters.hasTransactions}&`;
            }
            if (clientFilters.nationalId) {
                url += `nationalId=${clientFilters.nationalId}&`;
            }
            if (clientFilters.balanceType !== 'all') {
                url += `balanceType=${clientFilters.balanceType}&`;
            }
            
            // إضافة فلاتر التواريخ الجديدة
            if (clientFilters.netWorkDateFrom) {
                url += `netWorkDateFrom=${clientFilters.netWorkDateFrom}&`;
            }
            if (clientFilters.netWorkDateTo) {
                url += `netWorkDateTo=${clientFilters.netWorkDateTo}&`;
            }
            if (clientFilters.visaDateFrom) {
                url += `visaDateFrom=${clientFilters.visaDateFrom}&`;
            }
            if (clientFilters.visaDateTo) {
                url += `visaDateTo=${clientFilters.visaDateTo}&`;
            }
            if (clientFilters.travelDateFrom) {
                url += `travelDateFrom=${clientFilters.travelDateFrom}&`;
            }
            if (clientFilters.travelDateTo) {
                url += `travelDateTo=${clientFilters.travelDateTo}&`;
            }
        
            console.log('URL التقرير:', url);
            const response = await fetch(url, {
                signal: controller.signal  // إضافة إشارة الإلغاء
            });
            
            clearTimeout(timeoutId);  // مسح المؤقت عند نجاح الطلب
            
            if (!response.ok) throw new Error('فشل في جلب تقرير العملاء');
            
            const data = await response.json();
            setReportData(data.clients || []);
            
            // حساب الإحصائيات المجمعة
            if (data.clients?.length > 0) {
                const totalDeposits = data.clients.reduce((sum, client) => sum + (client.TotalDeposits || 0), 0);
                const totalWithdrawals = data.clients.reduce((sum, client) => sum + (client.TotalWithdrawals || 0), 0);
                const totalBalance = data.clients.reduce((sum, client) => sum + (client.CurrentBalance || 0), 0);
                
                setSummaryData({
                    totalClients: data.clients.length,
                    totalBalance,
                    totalDeposits,
                    totalWithdrawals
                });
            } else {
                setSummaryData({
                    totalClients: 0,
                    totalBalance: 0,
                    totalDeposits: 0,
                    totalWithdrawals: 0
                });
            }
            
            showToast('تم جلب التقرير بنجاح', 'success');
            
            // استدعاء دالة حساب المستحقات بعد جلب التقرير
            // باستخدام معالجة على مراحل لتحسين الأداء
            await calculateClientDuesInBatches(data.clients || []);
            
        } catch (error) {
            clearTimeout(timeoutId);  // تأكد من مسح المؤقت حتى في حالة الخطأ
            
            if (error.name === 'AbortError') {
                console.error('تم إلغاء الطلب بسبب انتهاء المهلة');
                showToast('استغرق تحميل البيانات وقتًا طويلاً. حاول مرة أخرى أو قم بتضييق نطاق البحث.', 'error');
            } else {
                console.error('خطأ في جلب تقرير العملاء:', error);
                showToast('فشل في جلب تقرير العملاء: ' + error.message, 'error');
            }
        } finally {
            setLoading(false);
        }
    };
    
    // دالة مساعدة لحساب المستحقات المالية على دفعات
    const calculateClientDuesInBatches = async (clients) => {
        try {
            // جلب بيانات مبالغ الدفع للشركات
            const companiesWithPayments = await fetchCompanyPaymentAmounts();
            
            // معالجة البيانات على دفعات لتحسين الأداء
            const batchSize = 20;
            const batches = Math.ceil(clients.length / batchSize);
            let processedClients = [];
            
            for (let i = 0; i < batches; i++) {
                const startIdx = i * batchSize;
                const endIdx = Math.min(startIdx + batchSize, clients.length);
                const batch = clients.slice(startIdx, endIdx);
                
                // معالجة دفعة واحدة
                const clientBatch = batch.map(client => {
                    const clientCompany = companiesWithPayments.find(
                        company => company.CompanyName === client.CompanyName
                    );
                    
                    const requiredPayment = clientCompany?.PaymentAmount || 0;
                    const paidAmount = client.TotalDeposits || 0;
                    const remainingDue = requiredPayment - paidAmount;
                    
                    return {
                        ...client,
                        RequiredPayment: requiredPayment,
                        PaidAmount: paidAmount,
                        RemainingDue: remainingDue
                    };
                });
                
                processedClients = [...processedClients, ...clientBatch];
                
                // تحديث تدريجي للواجهة
                if ((i + 1) % 2 === 0 || i === batches - 1) {
                    setClientDues([...processedClients]);
                }
                
                // إتاحة وقت للمتصفح للتفاعل مع المستخدم
                if (i < batches - 1) {
                    await new Promise(resolve => setTimeout(resolve, 10));
                }
            }
        } catch (error) {
            console.error('خطأ في حساب المستحقات المالية:', error);
            showToast('فشل في حساب المستحقات المالية', 'error');
        }
    };

    // جلب تقارير المندوبين
    const fetchDelegateReport = async () => {
        setLoading(true);
        try {
            let url = '/api/reports/delegates?';
            
            if (delegateFilters.delegateId) {
                url += `delegateId=${delegateFilters.delegateId}&`;
            }
            if (delegateFilters.dateFrom) {
                url += `dateFrom=${delegateFilters.dateFrom}&`;
            }
            if (delegateFilters.dateTo) {
                url += `dateTo=${delegateFilters.dateTo}&`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error('فشل في جلب تقرير المندوبين');
            
            const data = await response.json();
            setReportData(data.delegates || []);
            
            // حساب الإحصائيات
            if (data.delegates?.length > 0) {
                const totalDeposits = data.delegates.reduce((sum, delegate) => sum + (delegate.TotalDeposits || 0), 0);
                const totalWithdrawals = data.delegates.reduce((sum, delegate) => sum + (delegate.TotalWithdrawals || 0), 0);
                const totalBalance = data.delegates.reduce((sum, delegate) => sum + (delegate.CurrentBalance || 0), 0);
                
                setSummaryData({
                    totalClients: data.delegates.length,
                    totalBalance,
                    totalDeposits,
                    totalWithdrawals
                });
            } else {
                setSummaryData({
                    totalClients: 0,
                    totalBalance: 0,
                    totalDeposits: 0,
                    totalWithdrawals: 0
                });
            }
            
            showToast('تم جلب التقرير بنجاح');
        } catch (error) {
            console.error('خطأ في جلب تقرير المندوبين:', error);
            showToast('فشل في جلب تقرير المندوبين', 'error');
        } finally {
            setLoading(false);
        }
    };

    // جلب تقارير الشركة
    const fetchCompanyReport = async () => {
        setLoading(true);
        try {
            let url = '/api/reports/companies?';
            
            if (companyFilters.companyId) {
                url += `companyId=${companyFilters.companyId}&`;
            }
            if (companyFilters.dateFrom) {
                url += `dateFrom=${companyFilters.dateFrom}&`;
            }
            if (companyFilters.dateTo) {
                url += `dateTo=${companyFilters.dateTo}&`;
            }
            if (companyFilters.transactionType) {
                url += `transactionType=${companyFilters.transactionType}&`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error('فشل في جلب تقرير الشركات');
            
            const data = await response.json();
            setReportData(data.companies || []);
            
            // حساب الإحصائيات
            if (data.companies?.length > 0) {
                const totalDeposits = data.companies.reduce((sum, company) => sum + (company.TotalDeposits || 0), 0);
                const totalWithdrawals = data.companies.reduce((sum, company) => sum + (company.TotalWithdrawals || 0), 0);
                const totalBalance = data.companies.reduce((sum, company) => sum + (company.CurrentBalance || 0), 0);
                
                setSummaryData({
                    totalClients: data.companies.length,
                    totalBalance,
                    totalDeposits,
                    totalWithdrawals
                });
            } else {
                setSummaryData({
                    totalClients: 0,
                    totalBalance: 0,
                    totalDeposits: 0,
                    totalWithdrawals: 0
                });
            }
            
            showToast('تم جلب التقرير بنجاح');
        } catch (error) {
            console.error('خطأ في جلب تقرير الشركات:', error);
            showToast('فشل في جلب تقرير الشركات', 'error');
        } finally {
            setLoading(false);
        }
    };

    // جلب تقارير الخزينة
    const fetchTreasuryReport = async () => {
        setLoading(true);
        try {
            let url = '/api/reports/treasury?';
            
            if (treasuryFilters.dateFrom) {
                url += `dateFrom=${treasuryFilters.dateFrom}&`;
            }
            if (treasuryFilters.dateTo) {
                url += `dateTo=${treasuryFilters.dateTo}&`;
            }
            if (treasuryFilters.transactionType) {
                url += `transactionType=${treasuryFilters.transactionType}&`;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error('فشل في جلب تقرير الخزينة');
            
            const data = await response.json();
            setReportData(data.transactions || []);
            
            // حساب الإحصائيات
            if (data.summary) {
                setSummaryData({
                    totalClients: data.summary.TransactionsCount || 0,
                    totalBalance: data.summary.CurrentBalance || 0,
                    totalDeposits: data.summary.TotalDeposits || 0,
                    totalWithdrawals: data.summary.TotalWithdrawals || 0
                });
            } else {
                setSummaryData({
                    totalClients: 0,
                    totalBalance: 0,
                    totalDeposits: 0,
                    totalWithdrawals: 0
                });
            }
            
            showToast('تم جلب التقرير بنجاح');
        } catch (error) {
            console.error('خطأ في جلب تقرير الخزينة:', error);
            showToast('فشل في جلب تقرير الخزينة', 'error');
        } finally {
            setLoading(false);
        }
    };

    // تنفيذ البحث حسب التبويب النشط
    const executeSearch = () => {
        switch (activeTab) {
            case 'clients':
                fetchClientReport();
                break;
            case 'delegates':
                fetchDelegateReport();
                break;
            case 'company':
                fetchCompanyReport();
                break;
            case 'treasury':
                fetchTreasuryReport();
                break;
            default:
                break;
        }
    };

    // Export Functions
    const handleExportToExcel = () => {
        try {
            if (!reportData || reportData.length === 0) {
                showToast('لا توجد بيانات لتصديرها', 'error');
                return;
            }
    
            let dataToExport = [];
            let filename = '';
    
            switch (activeTab) {
                case 'clients':
                    // استخدام بيانات المستحقات إذا كانت متوفرة
                    const clientsData = clientDues.length > 0 ? clientDues : reportData;
                    
                    dataToExport = clientsData.map(client => ({
                        'اسم العميل': client.NameArabic || '-',
                        'الرقم القومي': client.NationalID || '-',
                        'الشركة': client.CompanyName || '-',
                        'المندوب': client.DelegateName || '-',
                        'المهنة': client.PassportProfession || '-',
                        'رقم الهاتف': formatPhoneNumber(client.PhoneNumbers),
                        // إضافة التواريخ الجديدة
                        'تاريخ النت': client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : '-',
                        'تاريخ التأشير': client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : '-',
                        'تاريخ السفر': client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-',
                        'إجمالي الإيداعات': client.TotalDeposits?.toLocaleString() || '0',
                        'إجمالي السحوبات': client.TotalWithdrawals?.toLocaleString() || '0',
                        'الرصيد الحالي': client.CurrentBalance?.toLocaleString() || '0',
                        // إضافة أعمدة المستحقات الجديدة
                        'المبلغ المطلوب': client.RequiredPayment?.toLocaleString() || '0',
                        'المتبقي': client.RemainingDue?.toLocaleString() || '0',
                        'الحالة': client.RemainingDue > 0 ? 'متبقي عليه مستحقات' : 'مسدد بالكامل'
                    }));
                    filename = 'تقرير_حسابات_العملاء';
                    break;
                
                case 'delegates':
                    dataToExport = reportData.map(delegate => ({
                        'اسم المندوب': delegate.DelegateName || '-',
                        'رقم الهاتف': delegate.PhoneNumber || '-',
                        'عدد العملاء': delegate.ClientsCount || '0',
                        'إجمالي الإيداعات': delegate.TotalDeposits?.toLocaleString() || '0',
                        'إجمالي السحوبات': delegate.TotalWithdrawals?.toLocaleString() || '0',
                        'الرصيد الحالي': delegate.CurrentBalance?.toLocaleString() || '0'
                    }));
                    filename = 'تقرير_حسابات_المندوبين';
                    break;
                
                case 'company':
                    dataToExport = reportData.map(company => ({
                        'اسم الشركة': company.CompanyName || '-',
                        'عدد العملاء': company.ClientsCount || '0',
                        'إجمالي الإيداعات': company.TotalDeposits?.toLocaleString() || '0',
                        'إجمالي السحوبات': company.TotalWithdrawals?.toLocaleString() || '0',
                        'الرصيد الحالي': company.CurrentBalance?.toLocaleString() || '0'
                    }));
                    filename = 'تقرير_الشركات';
                    break;
                
                case 'treasury':
                    dataToExport = reportData.map(transaction => ({
                        'التاريخ': new Date(transaction.TransactionDate).toLocaleDateString('ar-SA'),
                        'نوع المعاملة': transaction.TransactionType || '-',
                        'المبلغ': transaction.Amount?.toLocaleString() || '0',
                        'الرصيد': transaction.Balance?.toLocaleString() || '0',
                        'اسم العميل': transaction.ClientName || '-',
                        'الملاحظات': transaction.Notes || '-',
                        'تم بواسطة': transaction.CreatedBy || '-'
                    }));
                    filename = 'تقرير_الخزينة';
                    break;
                
                default:
                    break;
            }
    
            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "البيانات");
            XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            
            showToast('تم تصدير البيانات بنجاح');
        } catch (error) {
            console.error('خطأ في تصدير البيانات:', error);
            showToast('فشل في تصدير البيانات', 'error');
        }
    };

    const handlePrint = () => {
        try {
            window.print();
        } catch (error) {
            console.error('خطأ في الطباعة:', error);
            showToast('فشل في الطباعة', 'error');
        }
    };

    // نسخ رقم الهاتف
    const copyToClipboard = (text) => {
        if (!text || text === '-') return;
        
        navigator.clipboard.writeText(text)
            .then(() => {
                showToast('تم نسخ النص بنجاح');
            })
            .catch(err => {
                console.error('فشل في نسخ النص: ', err);
                showToast('فشل في نسخ النص', 'error');
            });
    };

    // عرض فلاتر العملاء
    const renderClientFilters = () => (
        <div className="space-y-4">
            {/* الصف الأول من الفلاتر - الشركة، المندوب، المهنة */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        الشركة
                    </label>
                    <select
                        value={clientFilters.companyId}
                        onChange={(e) => setClientFilters({...clientFilters, companyId: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">كل الشركات</option>
                        {companies.map(company => (
                            <option key={company.CompanyID} value={company.CompanyID}>
                                {company.CompanyName}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        المندوب
                    </label>
                    <select
                        value={clientFilters.delegateId}
                        onChange={(e) => setClientFilters({...clientFilters, delegateId: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">كل المندوبين</option>
                        {delegates.map(delegate => (
                            <option key={delegate.DelegateID} value={delegate.DelegateID}>
                                {delegate.DelegateName}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        المهنة
                    </label>
                    <select
                        value={clientFilters.professionId}
                        onChange={(e) => setClientFilters({...clientFilters, professionId: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">كل المهن</option>
                        {professions.map(profession => (
                        <option key={profession.ProfessionID} value={profession.ProfessionID}>
                            {profession.ProfessionName}
                        </option>
                    ))}
                </select>
            </div>
        </div>
        
        {/* الصف الثاني من الفلاتر - تاريخ المعاملات والرقم القومي */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ المعاملات (من)
                </label>
                <input
                    type="date"
                    value={clientFilters.dateFrom}
                    onChange={(e) => setClientFilters({...clientFilters, dateFrom: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ المعاملات (إلى)
                </label>
                <input
                    type="date"
                    value={clientFilters.dateTo}
                    onChange={(e) => setClientFilters({...clientFilters, dateTo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    الرقم القومي
                </label>
                <input
                    type="text"
                    placeholder="أدخل الرقم القومي للبحث"
                    value={clientFilters.nationalId}
                    onChange={(e) => setClientFilters({...clientFilters, nationalId: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>
        
        {/* الصف الثالث من الفلاتر - تاريخ النت */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ النت (من)
                </label>
                <input
                    type="date"
                    value={clientFilters.netWorkDateFrom}
                    onChange={(e) => setClientFilters({...clientFilters, netWorkDateFrom: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ النت (إلى)
                </label>
                <input
                    type="date"
                    value={clientFilters.netWorkDateTo}
                    onChange={(e) => setClientFilters({...clientFilters, netWorkDateTo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>

        {/* الصف الرابع من الفلاتر - تاريخ التأشير */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ التأشير (من)
                </label>
                <input
                    type="date"
                    value={clientFilters.visaDateFrom}
                    onChange={(e) => setClientFilters({...clientFilters, visaDateFrom: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ التأشير (إلى)
                </label>
                <input
                    type="date"
                    value={clientFilters.visaDateTo}
                    onChange={(e) => setClientFilters({...clientFilters, visaDateTo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>

        {/* الصف الخامس من الفلاتر - تاريخ السفر */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ السفر (من)
                </label>
                <input
                    type="date"
                    value={clientFilters.travelDateFrom}
                    onChange={(e) => setClientFilters({...clientFilters, travelDateFrom: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ السفر (إلى)
                </label>
                <input
                    type="date"
                    value={clientFilters.travelDateTo}
                    onChange={(e) => setClientFilters({...clientFilters, travelDateTo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
            </div>
        </div>

        {/* الصف السادس من الفلاتر - حالة المعاملات وحالة الرصيد */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    حالة المعاملات
                </label>
                <select
                    value={clientFilters.hasTransactions}
                    onChange={(e) => setClientFilters({...clientFilters, hasTransactions: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">الكل</option>
                    <option value="true">لديه معاملات</option>
                    <option value="false">ليس لديه معاملات</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    حالة الرصيد
                </label>
                <select
                    value={clientFilters.balanceType}
                    onChange={(e) => setClientFilters({...clientFilters, balanceType: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="all">الكل</option>
                    <option value="positive">رصيد إيجابي</option>
                    <option value="negative">رصيد سلبي</option>
                    <option value="zero">رصيد صفري</option>
                </select>
            </div>
        </div>
        
        <div className="flex justify-end gap-4">
            <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
                إعادة تعيين
            </button>
            <button
                type="button"
                onClick={executeSearch}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
                <Search size={16} />
                بحث
            </button>
        </div>
    </div>
);

    // عرض فلاتر المندوبين
    const renderDelegateFilters = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        المندوب
                    </label>
                    <select
                        value={delegateFilters.delegateId}
                        onChange={(e) => setDelegateFilters({...delegateFilters, delegateId: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">كل المندوبين</option>
                        {delegates.map(delegate => (
                            <option key={delegate.DelegateID} value={delegate.DelegateID}>
                                {delegate.DelegateName}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        من تاريخ
                    </label>
                    <input
                        type="date"
                        value={delegateFilters.dateFrom}
                        onChange={(e) => setDelegateFilters({...delegateFilters, dateFrom: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        إلى تاريخ
                    </label>
                    <input
                        type="date"
                        value={delegateFilters.dateTo}
                        onChange={(e) => setDelegateFilters({...delegateFilters, dateTo: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>
            
            <div className="flex justify-end gap-4">
                <button
                    type="button"
                    onClick={resetFilters}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    إعادة تعيين
                </button>
                <button
                    type="button"
                    onClick={executeSearch}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <Search size={16} />
                    بحث
                </button>
            </div>
        </div>
    );

    // عرض فلاتر الشركة
    const renderCompanyFilters = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        الشركة
                    </label>
                    <select
                        value={companyFilters.companyId}
                        onChange={(e) => setCompanyFilters({...companyFilters, companyId: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">كل الشركات</option>
                        {companies.map(company => (
                            <option key={company.CompanyID} value={company.CompanyID}>
                                {company.CompanyName}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        من تاريخ
                    </label>
                    <input
                        type="date"
                        value={companyFilters.dateFrom}
                        onChange={(e) => setCompanyFilters({...companyFilters, dateFrom: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        إلى تاريخ
                    </label>
                    <input
                        type="date"
                        value={companyFilters.dateTo}
                        onChange={(e) => setCompanyFilters({...companyFilters, dateTo: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        نوع المعاملة
                    </label>
                    <select
                        value={companyFilters.transactionType}
                        onChange={(e) => setCompanyFilters({...companyFilters, transactionType: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">الكل</option>
                        <option value="income">إيرادات</option>
                        <option value="expense">مصروفات</option>
                    </select>
                </div>
            </div>
            
            <div className="flex justify-end gap-4">
                <button
                    type="button"
                    onClick={resetFilters}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    إعادة تعيين
                </button>
                <button
                    type="button"
                    onClick={executeSearch}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <Search size={16} />
                    بحث
                </button>
            </div>
        </div>
    );

    // عرض فلاتر الخزينة
    const renderTreasuryFilters = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        من تاريخ
                    </label>
                    <input
                        type="date"
                        value={treasuryFilters.dateFrom}
                        onChange={(e) => setTreasuryFilters({...treasuryFilters, dateFrom: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        إلى تاريخ
                    </label>
                    <input
                        type="date"
                        value={treasuryFilters.dateTo}
                        onChange={(e) => setTreasuryFilters({...treasuryFilters, dateTo: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        نوع المعاملة
                    </label>
                    <select
                        value={treasuryFilters.transactionType}
                        onChange={(e) => setTreasuryFilters({...treasuryFilters, transactionType: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">الكل</option>
                        <option value="deposit">إيداع</option>
                        <option value="withdrawal">سحب</option>
                    </select>
                </div>
            </div>
            
            <div className="flex justify-end gap-4">
                <button
                    type="button"
                    onClick={resetFilters}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    إعادة تعيين
                </button>
                <button
                    type="button"
                    onClick={executeSearch}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <Search size={16} />
                    بحث
                </button>
            </div>
        </div>
    );

    // عرض محتوى التقرير حسب التبويب النشط
    const renderReportContent = () => {
        switch (activeTab) {
            case 'clients':
                return renderClientReport();
            case 'delegates':
                return renderDelegateReport();
            case 'company':
                return renderCompanyReport();
            case 'treasury':
                return renderTreasuryReport();
            default:
                return null;
        }
    };

    // عرض تقرير العملاء
    const renderClientReport = () => {
        if (!reportData || reportData.length === 0) {
            return (
                <div className="p-8 text-center">
                    <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
                </div>
            );
        }

        // الحصول على بيانات العملاء مع المستحقات
        const clientsData = clientDues.length > 0 ? clientDues : reportData;

        return (
            <>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">اسم العميل</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الرقم القومي</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الشركة</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المندوب</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المهنة</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رقم الهاتف</th>
                                {/* أعمدة التواريخ */}
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ النت</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ التأشير</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تاريخ السفر</th>
                                {/* أعمدة الإيداعات والسحوبات */}
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">إجمالي الإيداعات</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">إجمالي السحوبات</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الرصيد الحالي</th>
                                {/* أعمدة المستحقات الجديدة */}
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 bg-yellow-50">المبلغ المطلوب</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 bg-yellow-50">المتبقي</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 bg-yellow-50">الحالة</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {clientsData.map((client, index) => (
                                <tr key={index} className={`hover:bg-gray-50 ${
                                    // تلوين الصفوف التي عليها مستحقات
                                    client.RemainingDue > 0 ? 'bg-red-50' : ''
                                }`}>
                                    <td className="px-4 py-3 text-sm text-gray-900">{client.NameArabic || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{client.NationalID || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{client.CompanyName || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{client.DelegateName || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{client.PassportProfession || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <span>{formatPhoneNumber(client.PhoneNumbers)}</span>
                                            {formatPhoneNumber(client.PhoneNumbers) !== '-' && (
                                                <button 
                                                    onClick={() => copyToClipboard(formatPhoneNumber(client.PhoneNumbers))}
                                                    className="text-blue-500 hover:text-blue-700"
                                                    title="نسخ رقم الهاتف"
                                                >
                                                    <Copy size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    {/* عرض التواريخ */}
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        {client.NetWorkDate ? new Date(client.NetWorkDate).toLocaleDateString('ar-EG') : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        {client.VisaDate ? new Date(client.VisaDate).toLocaleDateString('ar-EG') : '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        {client.TravelDate ? new Date(client.TravelDate).toLocaleDateString('ar-EG') : '-'}
                                    </td>
                                    {/* الإيداعات والسحوبات */}
                                    <td className="px-4 py-3 text-sm text-green-600">
                                        {formatCurrency(client.TotalDeposits)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-red-600">
                                        {formatCurrency(client.TotalWithdrawals)}
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${
                                        client.CurrentBalance > 0 ? 'text-green-600' : 
                                        client.CurrentBalance < 0 ? 'text-red-600' : 'text-gray-600'
                                    }`}>
                                        {formatCurrency(client.CurrentBalance)}
                                    </td>
                                    {/* أعمدة المستحقات الجديدة */}
                                    <td className="px-4 py-3 text-sm text-gray-800 bg-yellow-50">
                                        {formatCurrency(client.RequiredPayment || 0)}
                                    </td>
                                    <td className={`px-4 py-3 text-sm bg-yellow-50 ${
                                        (client.RemainingDue || 0) > 0 ? 'text-red-600 font-bold' : 'text-green-600'
                                    }`}>
                                        {formatCurrency(client.RemainingDue || 0)}
                                    </td>
                                    <td className="px-4 py-3 text-sm bg-yellow-50">
                                        {(client.RemainingDue || 0) > 0 ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                متبقي {formatCurrency(client.RemainingDue)}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                مسدد بالكامل
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    // عرض تقرير المندوبين
    const renderDelegateReport = () => {
        if (!reportData || reportData.length === 0) {
            return (
                <div className="p-8 text-center">
                    <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
                </div>
            );
        }

        return (
            <>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">اسم المندوب</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رقم الهاتف</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">عدد العملاء</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">إجمالي الإيداعات</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">إجمالي السحوبات</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الرصيد الحالي</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {reportData.map((delegate, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">{delegate.DelegateName || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                        <span>{delegate.PhoneNumber || '-'}</span>
                                            {delegate.PhoneNumber && (
                                                <button 
                                                    onClick={() => copyToClipboard(delegate.PhoneNumber)}
                                                    className="text-blue-500 hover:text-blue-700"
                                                    title="نسخ رقم الهاتف"
                                                >
                                                    <Copy size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{delegate.ClientsCount || '0'}</td>
                                    <td className="px-4 py-3 text-sm text-green-600">
                                        {formatCurrency(delegate.TotalDeposits)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-red-600">
                                        {formatCurrency(delegate.TotalWithdrawals)}
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${
                                        delegate.CurrentBalance > 0 ? 'text-green-600' : 
                                        delegate.CurrentBalance < 0 ? 'text-red-600' : 'text-gray-600'
                                    }`}>
                                        {formatCurrency(delegate.CurrentBalance)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    // عرض تقرير الشركة
    const renderCompanyReport = () => {
        if (!reportData || reportData.length === 0) {
            return (
                <div className="p-8 text-center">
                    <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
                </div>
            );
        }

        return (
            <>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">اسم الشركة</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">عدد العملاء</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">إجمالي الإيداعات</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">إجمالي السحوبات</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الرصيد الحالي</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {reportData.map((company, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">{company.CompanyName || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{company.ClientsCount || '0'}</td>
                                    <td className="px-4 py-3 text-sm text-green-600">
                                        {formatCurrency(company.TotalDeposits)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-red-600">
                                        {formatCurrency(company.TotalWithdrawals)}
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${
                                        company.CurrentBalance > 0 ? 'text-green-600' : 
                                        company.CurrentBalance < 0 ? 'text-red-600' : 'text-gray-600'
                                    }`}>
                                        {formatCurrency(company.CurrentBalance)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    // عرض تقرير الخزينة
    const renderTreasuryReport = () => {
        if (!reportData || reportData.length === 0) {
            return (
                <div className="p-8 text-center">
                    <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">لا توجد بيانات متاحة. يرجى تحديد معايير البحث والضغط على زر البحث.</p>
                </div>
            );
        }

        return (
            <>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">التاريخ</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">نوع المعاملة</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">المبلغ</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الرصيد</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">اسم العميل</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">الملاحظات</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">تم بواسطة</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {reportData.map((transaction, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                        {new Date(transaction.TransactionDate).toLocaleDateString('ar-SA')}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                            ${transaction.TransactionType === 'إيداع' ? 
                                            'bg-green-100 text-green-800' : 
                                            'bg-red-100 text-red-800'}`}>
                                            {transaction.TransactionType === 'إيداع' ? (
                                                <TrendingUp size={14} className="mr-1" />
                                            ) : (
                                                <TrendingDown size={14} className="mr-1" />
                                            )}
                                            {transaction.TransactionType}
                                        </span>
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${
                                        transaction.TransactionType === 'إيداع' ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        {formatCurrency(transaction.Amount)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                        {formatCurrency(transaction.Balance)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{transaction.ClientName || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{transaction.Notes || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{transaction.CreatedBy || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    // عرض الإحصائيات
    const renderSummary = () => {
        if (!reportData || reportData.length === 0) return null;

        return (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-4 text-white">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm opacity-80">
                                {activeTab === 'clients' ? 'عدد العملاء' : 
                                 activeTab === 'delegates' ? 'عدد المندوبين' : 
                                 activeTab === 'company' ? 'عدد الشركات' : 'عدد المعاملات'}
                            </p>
                            <h3 className="text-2xl font-bold mt-1">
                                {summaryData.totalClients}
                            </h3>
                        </div>
                        <div className="opacity-20">
                            {activeTab === 'clients' ? <Users size={36} /> : 
                             activeTab === 'delegates' ? <Briefcase size={36} /> : 
                             activeTab === 'company' ? <Building size={36} /> : <FileText size={36} />}
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-4 text-white">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm opacity-80">إجمالي الإيداعات</p>
                            <h3 className="text-2xl font-bold mt-1">
                                {summaryData.totalDeposits?.toLocaleString()} ريال
                            </h3>
                        </div>
                        <TrendingUp className="h-9 w-9 opacity-20" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-4 text-white">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm opacity-80">إجمالي السحوبات</p>
                            <h3 className="text-2xl font-bold mt-1">
                                {summaryData.totalWithdrawals?.toLocaleString()} ريال
                            </h3>
                        </div>
                        <TrendingDown className="h-9 w-9 opacity-20" />
                    </div>
                </div>

                <div className={`rounded-xl shadow-lg p-4 text-white
                    ${summaryData.totalBalance >= 0 ? 
                      'bg-gradient-to-br from-purple-500 to-purple-600' : 
                      'bg-gradient-to-br from-red-500 to-red-600'}`}>
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-sm opacity-80">الرصيد الكلي</p>
                            <h3 className="text-2xl font-bold mt-1">
                                {summaryData.totalBalance?.toLocaleString()} ريال
                            </h3>
                        </div>
                        <DollarSign className="h-9 w-9 opacity-20" />
                    </div>
                </div>
            </div>
        );
    };

    // المكون الرئيسي
    return (
        <div className="min-h-screen bg-gray-50 py-6" dir="rtl">
            <div className="max-w-7xl mx-auto px-4">
                {/* شريط التنقل */}
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-700 transition-colors"
                        >
                            <ArrowRight size={20} />
                            العودة للرئيسية
                        </Link>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <BarChart2 size={24} className="text-blue-600" />
                        التقارير المالية
                    </h1>
                </div>

                {/* تبويبات التقارير */}
                <div className="bg-white rounded-xl shadow-md mb-6 overflow-hidden">
                    <div className="flex border-b">
                        <button
                            onClick={() => setActiveTab('clients')}
                            className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors
                                     ${activeTab === 'clients' ? 
                                      'text-blue-600 border-b-2 border-blue-600' : 
                                      'text-gray-500 hover:text-blue-600'}`}
                        >
                            <Users size={16} />
                            حسابات العملاء
                        </button>
                        <button
                            onClick={() => setActiveTab('delegates')}
                            className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors
                                     ${activeTab === 'delegates' ? 
                                      'text-blue-600 border-b-2 border-blue-600' : 
                                      'text-gray-500 hover:text-blue-600'}`}
                        >
                            <Briefcase size={16} />
                            حسابات المندوبين
                        </button>
                        <button
                            onClick={() => setActiveTab('company')}
                            className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors
                                ${activeTab === 'company' ? 
                                 'text-blue-600 border-b-2 border-blue-600' : 
                                 'text-gray-500 hover:text-blue-600'}`}
                   >
                       <Building size={16} />
                       إيرادات ومصروفات الشركة
                   </button>
                   <button
                       onClick={() => setActiveTab('treasury')}
                       className={`px-6 py-3 text-sm font-medium flex items-center gap-2 transition-colors
                                ${activeTab === 'treasury' ? 
                                 'text-blue-600 border-b-2 border-blue-600' : 
                                 'text-gray-500 hover:text-blue-600'}`}
                   >
                       <DollarSign size={16} />
                       الخزينة
                   </button>
               </div>
           </div>

           {/* شريط الأدوات */}
           <div className="bg-white rounded-xl shadow-md p-4 mb-6">
               <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                   <div className="flex items-center gap-4">
                       <button
                           onClick={() => setShowFilters(!showFilters)}
                           className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg 
                                  flex items-center gap-2 hover:bg-gray-200 transition-colors"
                       >
                           <Filter size={18} />
                           معايير التصفية
                           <ChevronDown size={18} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                       </button>

                       <button
                           onClick={executeSearch}
                           className="bg-blue-600 text-white px-4 py-2 rounded-lg 
                                  flex items-center gap-2 hover:bg-blue-700 transition-colors"
                       >
                           <RefreshCw size={18} />
                           تحديث
                       </button>

                       {/* زر حساب المستحقات المالية */}
                       {activeTab === 'clients' && (
                           <button
                               onClick={calculateClientDues}
                               className="bg-yellow-600 text-white px-4 py-2 rounded-lg 
                                      flex items-center gap-2 hover:bg-yellow-700 transition-colors"
                           >
                               <DollarSign size={18} />
                               حساب المستحقات المالية
                           </button>
                       )}

                       <button
                           onClick={handleExportToExcel}
                           className="bg-green-600 text-white px-4 py-2 rounded-lg 
                                  flex items-center gap-2 hover:bg-green-700 transition-colors"
                           disabled={!reportData || reportData.length === 0}
                       >
                           <Download size={18} />
                           تصدير Excel
                       </button>

                       <button
                           onClick={handlePrint}
                           className="bg-purple-600 text-white px-4 py-2 rounded-lg 
                                  flex items-center gap-2 hover:bg-purple-700 transition-colors"
                           disabled={!reportData || reportData.length === 0}
                       >
                           <Printer size={18} />
                           طباعة
                       </button>
                   </div>
               </div>

               {showFilters && (
                   <div className="p-4 border-t border-gray-200">
                       {activeTab === 'clients' && renderClientFilters()}
                       {activeTab === 'delegates' && renderDelegateFilters()}
                       {activeTab === 'company' && renderCompanyFilters()}
                       {activeTab === 'treasury' && renderTreasuryFilters()}
                   </div>
               )}
           </div>

           {/* الإحصائيات */}
           {renderSummary()}

           {/* محتوى التقرير */}
           <div className="bg-white rounded-xl shadow-md overflow-hidden" ref={printRef}>
               <div className="p-4 border-b">
                   <h2 className="text-lg font-semibold flex items-center gap-2">
                       {activeTab === 'clients' ? (
                           <>
                               <Users size={18} className="text-blue-600" />
                               تقرير حسابات العملاء
                           </>
                       ) : activeTab === 'delegates' ? (
                           <>
                               <Briefcase size={18} className="text-blue-600" />
                               تقرير حسابات المندوبين
                           </>
                       ) : activeTab === 'company' ? (
                           <>
                               <Building size={18} className="text-blue-600" />
                               تقرير إيرادات ومصروفات الشركة
                           </>
                       ) : (
                           <>
                               <DollarSign size={18} className="text-blue-600" />
                               تقرير الخزينة
                           </>
                       )}
                   </h2>
                   <p className="text-sm text-gray-500 mt-1">
                       تم إنشاء التقرير بتاريخ {new Date().toLocaleDateString('ar-SA')}
                   </p>
               </div>

               {loading ? (
                   <div className="p-8 text-center">
                       <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
                       <p className="text-gray-500">جاري تحميل البيانات...</p>
                   </div>
               ) : (
                   renderReportContent()
               )}
           </div>

           {/* رسائل النجاح والخطأ */}
           {toast && (
               <div className={`
                   fixed bottom-4 right-4 z-50 
                   p-4 rounded-lg shadow-lg 
                   flex items-center gap-3
                   animate-slide-up
                   ${toast.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
               `}>
                   {toast.message}
                   <button 
                       onClick={() => setToast(null)} 
                       className="ml-3 hover:opacity-75"
                   >
                       <X size={16} />
                   </button>
               </div>
           )}
       </div>

       {/* أضف CSS الإضافي للرسوم المتحركة */}
       <style dangerouslySetInnerHTML={{
           __html: `
           @keyframes slideUp {
               from { opacity: 0; transform: translateY(20px); }
               to { opacity: 1; transform: translateY(0); }
           }
           .animate-slide-up {
               animation: slideUp 0.3s ease-out forwards;
           }
           
           @media print {
               body * {
                   visibility: hidden;
               }
               #printRef, #printRef * {
                   visibility: visible;
               }
               #printRef {
                   position: absolute;
                   left: 0;
                   top: 0;
                   width: 100%;
               }
           }
           `
       }} />
   </div>
);
};

export default FinancialReports;